/**
 * @file PlanApprovalReview.jsx
 * @module ui/src/components/admin/PlanApprovalReview
 * @description
 *   Renders the full CA-2 review of a newly submitted compensation plan around
 *   document economics, coverage/effective dates, projected consequence, runtime
 *   data context, and governance lineage.
 *
 * @version 1.0.0
 * @since S756
 * @author data-fit LLC · DecisionFlow · S777
 *
 * @architecture
 *   Locked decisions : #1, #2, #6, #17, #25
 *   Temporal invariants : INV-1, INV-3, INV-4
 *   Design principles : DP-1, DP-2, DP-4, DP-5, DP-7, DP-8, DP-10, DP-12, DP-13, DP-14
 *   Cross-cutting contracts : XC-1, XC-2, XC-4, XC-5, XC-6, XC-7
 *
 * @domain-rules
 *   - PlanNarrativeSummary is an understanding/navigation aid only; structured economic sections remain the authoritative approval object.
 *   - CA-2 approves the complete plan document once; no economic layer or modifier receives an independent approval action.
 *   - Economic applicability is driven by server-provided effective dates rather than plan_status or approval timestamp.
 *   - Data readiness and data freshness describe runtime context and are categorically separate from document validity.
 *   - Human-readable server labels are displayed instead of raw spine codes, rate table identifiers, or inferred economic semantics.
 *   - Layer 2 modifiers are ordered by the locked DP-7 canonical sequence.
 *   - Projected payout distinguishes calculated zero, no result, and constrained/partial result.
 *   - Approval eligibility is rendered from the server payload and never recomputed client-side.
 *   - Approval remains server-authoritative; onApprove fires only after HTTP 200.
 *
 * @wiring-s777
 *   ep 22 GET /api/v1/plans/{id}/approval-review — WIRED S777
 *   ep 9  POST /api/v1/plans/{id}/approve — WIRED (unchanged — HTTP 200 ✅)
 *   OI-S774-PLAN-APPROVAL-REVIEW-SHAPE closed S777:
 *     economics{}/coverage{}/governance{} — invented shapes removed; router fields used directly.
 *     eligibility → callerEligibility{eligible, blockedReason}
 *     effectiveFrom/To (flat) → temporalRelationship{effectiveFrom, effectiveTo}
 *     governanceHistory[].at confirmed (not occurredAt)
 *     crlOverride = null (not a bool) — handled gracefully
 *     economicImpact.freshnessAt (not projectionFreshnessAt)
 *     economicImpact.positionCount (not affectedPositionCount)
 *     resolvedPopulation{resolvedCount, coverageExceptions[]} — replaces coverage.resolvedPopulationCount
 *     dataContext{quotaFreshness, creditFreshness, populationReadiness} — nested objects
 *     planSummary — NOT in router; PlanNarrativeSummary receives null gracefully
 *     layer2Modifiers — NOT in router; orderedModifiers will be empty (router returns raw layer2Config jsonb)
 *
 * @migration-flags
 *   - plan.effective_from / plan.effective_to — pending m_tc_01 (Neon FROZEN)
 *   - economicImpact.projectedTotalPayout — null (OI-S760-PAYOUT-SUMMARY-SCHEMA open)
 *   - dataContext sub-objects — partially null (future session joins)
 *   - planSummary — NOT in ep 22 response (OI-S753-PLAN-SUMMARY-GEN-01 carried)
 *   - layer2Modifiers — NOT in ep 22 response (router returns raw layer2Config jsonb)
 *
 * @changelog
 *   1.0.0  S756  Initial build — Group CA-2 batch delivery
 *   1.1.0  S777  Wired ep 22. Fixed 7 structural mismatches. Removed AI ref. CRLF→LF.
 *   1.2.0  S900  OI-S900-AMENDMENT-APPROVE-WRONG-ENDPOINT fix. amendmentId optional prop added.
 *                handleApprove() now routes to POST /plans/{id}/amendments/{aid}/approve when
 *                amendmentId is present (amendment target), EP9 otherwise (standalone plan).
 *                AD-66 + OD-18: EP9 server-rejects amendment targets with 409. Routing is
 *                authoritative — never inferred client-side from version or status.
 */

import PropTypes from 'prop-types';
import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import ApprovalEligibilityGate from '../shared/ApprovalEligibilityGate';
import DataFreshnessIndicator from '../shared/DataFreshnessIndicator';
import PlanNarrativeSummary from '../shared/PlanNarrativeSummary';
import ProvisionalBadge from '../shared/ProvisionalBadge';

/**
 * @typedef {Object} ApprovalScopePayload
 * @property {string} scopeId
 * @property {string} scopeType
 * @property {string[]} coveredBuIds
 * @property {boolean} isGlobalApprover
 */

/**
 * @typedef {Object} Layer2ModifierSummary
 * @property {string} modifierType
 * @property {string} label
 * @property {boolean} isEnabled
 * @property {string} configSummary
 */

/**
 * @typedef {Object} PlanApprovalReviewProps
 * @property {number} planId
 * @property {Object} currentUser
 * @property {ApprovalScopePayload} approvalScope
 * @property {Function} onApprove
 * @property {Function} onReturn
 */

// OI-CA2-APPROVAL-SCREEN-ENRICHMENT S1022: human-readable display names for PCMA modifier types.
// Raw DB enum values (P3B-CAP, P3B-CLAWBACK etc.) are not suitable for CA-2 review surface.
const PCMA_MODIFIER_DISPLAY_NAMES = {
  'P3B-REVTYPE':   'Revenue Type Differentials',
  'P3B-KICKER':    'Kicker / Multiplier',
  'P3B-CAP':       'Period Cap',
  'P3B-CLAWBACK':  'Churn Clawback',
  'P3B-DRAW':      'Draw / Guarantee',
  'P3B-SPIFF':     'SPIFF / Tactical Incentive',
  'P3B-TXN':       'Transaction Commission',
  'P3B-MULTIYEAR': 'Multi-Year Deal',
  'P4-MARKET':     'Market Share',
  'P4-PHARMA':     'Pharma Script',
  'P4-POD':        'Pod / Team Payout',
  'P4-RETENTION':  'Retention / GRR',
  'P4-VOLUME':     'Volume / Quantity',
};

// Inline dl row style: label left, value right on same line, bottom border separator.
const dlRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: '16px',
  padding: '6px 0',
  borderBottom: '1px solid rgb(var(--color-border))',
};
const dtStyle = {
  color: 'rgb(var(--color-text-muted))',
  fontSize: '0.875rem',
  flexShrink: 0,
  minWidth: '140px',
};
const ddStyle = {
  margin: 0,
  textAlign: 'right',
  fontWeight: 500,
};
// These are the exact modifierType values written to plan.layer2_config by the PATCH router.
// Stale aliases (revenue_type, multiyear, noncash, guarantee, settlement) removed — they do
// not match what the server writes and would silently push all modifiers to the end of the list.
const MODIFIER_CANONICAL_ORDER = [
  'rev_type_differentials',
  'kicker',
  'cap',
  'draw_guarantee',
  'clawback',
  'spiff',
  'contest',
  'non_cash',
  'multi_year_deal',
];

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const normalizeModifierKey = (modifierType) => (
  String(modifierType || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
);

const modifierRank = (modifierType) => {
  const normalized = normalizeModifierKey(modifierType);
  const index = MODIFIER_CANONICAL_ORDER.indexOf(normalized);

  return index === -1 ? MODIFIER_CANONICAL_ORDER.length : index;
};

const renderProjectedPayout = (economicImpact) => {
  // WHY: ep 22 router returns isProvisional bool, not payoutState string.
  // projectedTotalPayout is null when OI-S760-PAYOUT-SUMMARY-SCHEMA not resolved.
  if (
    !economicImpact
    || economicImpact.projectedTotalPayout === null
    || economicImpact.projectedTotalPayout === undefined
  ) {
    return 'Projected payout not available';
  }

  // WHY: isProvisional=true indicates the projection is incomplete — communicate this explicitly.
  if (economicImpact.isProvisional) {
    return `${currencyFormatter.format(economicImpact.projectedTotalPayout)} — provisional`;
  }

  // WHY: available numeric projection preserves exactly zero as a valid calculated payout.
  return currencyFormatter.format(economicImpact.projectedTotalPayout ?? 0);
};

const modifierBadge = (modifier) => {
  // WHY: enabled state is expressed with explicit text rather than inferred from visual styling.
  if (modifier.isEnabled) {
    return {
      label: 'Enabled',
      className: 'modifier-manager-badge modifier-manager-badge--complete',
    };
  }

  const summary = String(modifier.configSummary || '').toLowerCase();
  const unavailable = summary.includes('not available');

  // WHY: a server-described unavailable capability remains distinct from an available modifier that simply has no configuration.
  if (unavailable) {
    return {
      label: 'Not available',
      className: 'modifier-manager-badge modifier-manager-badge--readonly',
    };
  }

  return {
    label: 'Not configured',
    className: 'modifier-manager-badge modifier-manager-badge--not-created',
  };
};

/**
 * Full CA-2 new-plan approval review.
 *
 * @param {PlanApprovalReviewProps} props
 * @returns {JSX.Element}
 */
function PlanApprovalReview({
  planId,
  amendmentId,
  onApprove,
  onReturn,
}) {
  // WHY: structured sections remain independently inspectable while Economics opens by default as the authoritative first approval question.
  const [sectionExpanded, setSectionExpanded] = useState({
    economics: true,
    coverage: false,
    impact: false,
    dataContext: false,
    governance: false,
  });

  // WHY: every document, economic, coverage, runtime, governance, and eligibility fact is consumed from the server review payload.
  const [reviewData, setReviewData] = useState(null);

  // WHY: loading state distinguishes an in-flight authoritative review from an actually empty plan document.
  const [loading, setLoading] = useState(false);

  // WHY: read or mutation failures stay visible without manufacturing document or workflow state.
  const [error, setError] = useState('');

  // WHY: approving disables duplicate governance mutation until the server confirms or rejects the request.
  const [approving, setApproving] = useState(false);

  // WHY: the complete approval-review payload is reloaded whenever plan identity changes.
  useEffect(() => {
    let active = true;

    const loadReview = async () => {
      setLoading(true);
      setError('');

      try {
        // WHY: the server endpoint owns authoritative economic labels, temporal data, readiness, lineage, and eligibility.
        const response = await fetch(
          `/api/v1/plans/${planId}/approval-review`,
          {
            method: 'GET',
            headers: {
              Accept: 'application/json',
            },
          },
        );

        // WHY: a failed authoritative read cannot be replaced with client inference from raw plan configuration.
        if (!response.ok) {
          throw new Error('Unable to load plan approval review.');
        }

        const payload = await response.json();

        // WHY: stale asynchronous responses must not mutate an unmounted or plan-switched review surface.
        if (active) {
          setReviewData(payload);
        }
      } catch (requestError) {
        // WHY: only the active plan review receives its corresponding fetch failure.
        if (active) {
          setError(
            requestError.message || 'Unable to load plan approval review.',
          );
        }
      } finally {
        // WHY: loading state belongs only to the currently active review identity.
        if (active) {
          setLoading(false);
        }
      }
    };

    loadReview();

    return () => {
      active = false;
    };
  }, [planId]);

  // WHY: ep 22 returns layer2Config as raw jsonb array. Parse and sort by canonical order.
  // Each entry has modifierType/type, isEnabled/enabled, and optional config object.
  // OI-CA2-APPROVAL-SCREEN-ENRICHMENT S1022: orderedModifiers retained for backward compat
  // but superseded by pcmaModifierSummary (see below) which reflects actual DB state.
  const orderedModifiers = useMemo(() => {
    const raw = reviewData?.layer2Config;
    if (!raw) return [];
    const arr = Array.isArray(raw) ? raw : (typeof raw === 'object' ? [raw] : []);
    return arr
      .filter(Boolean)
      .map((mod) => ({
        modifierType: mod.modifierType ?? mod.modifier_type ?? mod.type ?? '',
        label:        mod.label        ?? mod.modifierType  ?? mod.type ?? 'Modifier',
        isEnabled:    mod.isEnabled    ?? mod.is_enabled    ?? mod.enabled ?? false,
        configSummary: mod.configSummary ?? mod.summary ?? null,
      }))
      .sort((a, b) => modifierRank(a.modifierType) - modifierRank(b.modifierType));
  }, [reviewData]);

  // OI-CA2-APPROVAL-SCREEN-ENRICHMENT S1022:
  // pcmaModifierSummary from ep 22 — authoritative modifier state from DB.
  // Each entry: {modifierType, applicability, modifierStatus}.
  // Replaces layer2Config jsonb which only reflects design intent, not actual state.
  const pcmaModifierSummary = useMemo(() => (
    Array.isArray(reviewData?.pcmaModifierSummary) ? reviewData.pcmaModifierSummary : []
  ), [reviewData]);

  // OI-CA2-APPROVAL-SCREEN-ENRICHMENT S1022: plan component economics from ep 22.
  const planComponentEconomics = reviewData?.planComponentEconomics ?? null;

  // WHY: section disclosure changes inspectability only and never determines document validity, temporal applicability, or approval eligibility.
  const handleToggleSection = (sectionKey) => {
    setSectionExpanded((previous) => ({
      ...previous,
      [sectionKey]: !previous[sectionKey],
    }));
  };

  // WHY: plan approval is one complete-document server mutation and onApprove fires only after HTTP 200.
  const handleApprove = async () => {
    if (approving) {
      return;
    }

    setApproving(true);
    setError('');

    try {
      // WHY: amendment targets must be approved via EP14 (approvals_router.py
      // POST /plans/{id}/amendments/{aid}/approve). EP9 is server-locked against
      // amendment targets via OD-18 guard — it returns 409 PLAN_IS_AMENDMENT_TARGET.
      // Routing is determined by amendmentId prop supplied by the approval queue
      // parent (which has the amendmentId from the queue item row). Never inferred
      // from version, status, or any client-side heuristic. AD-66 + OD-18. S900.
      const approveUrl = amendmentId
        ? `/api/v1/plans/${planId}/amendments/${amendmentId}/approve`
        : `/api/v1/plans/${planId}/approve`;

      const response = await fetch(
        approveUrl,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        },
      );

      // WHY: non-200 responses leave the plan workflow state untouched in the client.
      if (response.status !== 200) {
        throw new Error('Unable to approve this plan.');
      }

      // WHY: parent orchestration is notified only after the server confirms the governance mutation.
      onApprove();
    } catch (requestError) {
      setError(requestError.message || 'Unable to approve this plan.');
    } finally {
      // WHY: clearing mutation progress permits retry after failure without asserting a local approval outcome.
      setApproving(false);
    }
  };

  // WHY: Return for Changes delegates structured rationale capture and its mutation to C-29.
  const handleReturn = () => {
    onReturn();
  };

  // WHY: initial loading is rendered explicitly so an absent server payload is not confused with a valid empty plan.
  if (loading && !reviewData) {
    return (
      <section className="modifier-manager-card" aria-busy="true">
        <header className="modifier-manager-header">
          <h2>Plan approval review</h2>
        </header>
        <div className="modifier-manager-body">
          <div className="skeleton" style={{ height: '90px' }} />
          <div className="skeleton" style={{ height: '130px' }} />
          <div className="skeleton" style={{ height: '90px' }} />
        </div>
      </section>
    );
  }

  // WHY: an initial read failure blocks reliable complete-document review and uses the established error surface.
  if (error && !reviewData) {
    return (
      <div className="modifier-manager-error-state" role="alert">
        {error}
      </div>
    );
  }

  // WHY: missing review data is made explicit and never interpreted as a complete or economically valid plan.
  if (!reviewData) {
    return (
      <div className="modifier-manager-empty-state">
        Plan approval review is not available.
      </div>
    );
  }

  // WHY: ep 22 router fields — confirmed S777 from approvals_router.py.
  // economics{}/coverage{}/governance{} did not exist in the router response.
  // All data access paths remapped to actual router response shape.

  // Identity + spine — top-level router fields
  const primarySpine = reviewData.primarySpine || null;

  // Temporal — nested object: temporalRelationship{effectiveFrom, effectiveTo}
  const temporalRelationship = reviewData.temporalRelationship || {};

  // Assignment scope — nested: assignmentScope{roleId, segmentId}
  const assignmentScope = reviewData.assignmentScope || {};

  // Economic impact — nested: {projectedTotalPayout, projectedPayoutCurrency, positionCount, freshnessAt, isProvisional}
  const impact = reviewData.economicImpact || {};

  // Resolved population — nested: {resolvedCount, coverageExceptions[]}
  const resolvedPopulation = reviewData.resolvedPopulation || {};

  // Data context — nested: {quotaFreshness, creditFreshness, populationReadiness}
  const dataContext = reviewData.dataContext || {};

  // Governance history — array of {eventType, actorId, at}
  const governanceHistory = reviewData.governanceHistory || [];

  // crlOverride — null in current router implementation
  const crlOverride = reviewData.crlOverride;

  // Caller eligibility — map EP22 shape {eligible, blockedReason} to
  // ApprovalEligibilityGate expected shape {canApprove, reasonCode}.
  // WHY: EP22 returns {eligible: bool, blockedReason: string|null} (router-confirmed S777).
  // ApprovalEligibilityGate reads {canApprove: bool, reasonCode: string|null}.
  // Without this mapping, canApprove was always undefined → falsy → gate always blocked.
  const callerEligibility = {
    canApprove: reviewData.callerEligibility?.eligible ?? false,
    reasonCode: reviewData.callerEligibility?.blockedReason ?? null,
  };

  // planSummary — NOT in ep 22 router response. PlanNarrativeSummary receives null gracefully.
  const planSummary = reviewData.planSummary || null;

  // layer2Modifiers — NOT in ep 22 router response. Router returns raw layer2Config jsonb.
  // orderedModifiers will be empty until a dedicated layer2 summary field is added to the endpoint.
  const blockingCount = 0; // dataContext sub-objects are partially null; no blockingCount in current router response

  return (
    <section className="modifier-manager-card">
      <header className="modifier-manager-header">
        <div>
          <h2>Plan approval review</h2>
          <p>
            <strong>{reviewData.planName}</strong> · {reviewData.planCode}
          </p>

          <span className="modifier-manager-badge modifier-manager-badge--readonly">
            Workflow status: {reviewData.planStatus || 'Not specified'}
          </span>
        </div>
      </header>

      <div className="modifier-manager-body">
        <section aria-labelledby="plan-narrative-summary">
          <h3 id="plan-narrative-summary">Plan summary</h3>

          <p className="modifier-manager-info">
            Plan summary — for navigation and understanding.
            The structured sections below are authoritative.
          </p>

          {/* WHY: planSummary is NOT in ep 22 router response. PlanNarrativeSummary receives null gracefully.
              OI-S753-PLAN-SUMMARY-GEN-01 carried — endpoint extension needed. */}
          <PlanNarrativeSummary
            planSummaryPayload={planSummary}
            persona="ca2"
            showGovernanceRationale
          />
        </section>

        <section aria-labelledby="plan-economics">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              alignItems: 'center',
            }}
          >
            <h3 id="plan-economics">What does this plan do?</h3>
            <button
              type="button"
              className="text-button"
              aria-expanded={sectionExpanded.economics}
              aria-controls="plan-economics-content"
              onClick={() => handleToggleSection('economics')}
            >
              {sectionExpanded.economics ? 'Hide economics' : 'Show economics'}
            </button>
          </div>

          {/* WHY: economics is authoritative approval content and opens by default rather than relying on the generated narrative. */}
          {sectionExpanded.economics && (
            <div id="plan-economics-content">
              <dl style={{ margin: '12px 0 20px', padding: 0 }}>
                <div style={dlRowStyle}>
                  <dt style={dtStyle}>Primary spine</dt>
                  {/* WHY: ep 22 returns primarySpine as top-level field not economics.primarySpine. Router-confirmed S777. */}
                  <dd style={ddStyle}>{primarySpine || 'Not specified'}</dd>
                </div>

                <div style={dlRowStyle}>
                  <dt style={dtStyle}>Credit allocation</dt>
                  <dd style={ddStyle}>
                    {reviewData.layer0Config
                      ? 'Layer 0 configured'
                      : 'Individual (no Layer 0)'}
                  </dd>
                </div>

                <div style={dlRowStyle}>
                  <dt style={dtStyle}>Rate table</dt>
                  <dd style={ddStyle}>{reviewData.rateTableName || 'Not configured'}</dd>
                </div>

                {planComponentEconomics && (
                  <>
                    {(planComponentEconomics.oteMin != null || planComponentEconomics.oteMax != null) && (
                      <div style={dlRowStyle}>
                        <dt style={dtStyle}>OTE range</dt>
                        <dd style={ddStyle}>
                          {planComponentEconomics.oteCurrencyCode
                            ? `${planComponentEconomics.oteCurrencyCode} `
                            : ''}
                          {planComponentEconomics.oteMin != null
                            ? planComponentEconomics.oteMin.toLocaleString()
                            : '—'}
                          {' – '}
                          {planComponentEconomics.oteMax != null
                            ? planComponentEconomics.oteMax.toLocaleString()
                            : '—'}
                          {planComponentEconomics.oteMidpoint != null && (
                            <span style={{ color: 'rgb(var(--color-text-muted))', marginInlineStart: '6px', fontWeight: 400 }}>
                              (mid {planComponentEconomics.oteMidpoint.toLocaleString()})
                            </span>
                          )}
                        </dd>
                      </div>
                    )}
                    {planComponentEconomics.basePct != null && (
                      <div style={dlRowStyle}>
                        <dt style={dtStyle}>Base / Variable</dt>
                        <dd style={ddStyle}>
                          {Math.round(planComponentEconomics.basePct * 100)}%
                          {' / '}
                          {Math.round(planComponentEconomics.variablePct * 100)}%
                        </dd>
                      </div>
                    )}
                    {planComponentEconomics.arrWeight != null && planComponentEconomics.arrWeight < 1 && (
                      <div style={dlRowStyle}>
                        <dt style={dtStyle}>ARR / MBO weight</dt>
                        <dd style={ddStyle}>
                          {Math.round(planComponentEconomics.arrWeight * 100)}%
                          {' / '}
                          {Math.round(planComponentEconomics.mboWeight * 100)}%
                        </dd>
                      </div>
                    )}
                  </>
                )}

                {reviewData.libraryPackageCode && (
                  <div style={dlRowStyle}>
                    <dt style={dtStyle}>Deployed from</dt>
                    <dd style={ddStyle}>{reviewData.libraryPackageCode}</dd>
                  </div>
                )}

                <div style={{ ...dlRowStyle, borderBottom: 'none' }}>
                  <dt style={dtStyle}>Fiscal period</dt>
                  <dd style={ddStyle}>
                    {reviewData.fiscalPeriodLabel || reviewData.fiscalPeriodCode || reviewData.fiscalPeriodId || 'Not specified'}
                  </dd>
                </div>
              </dl>

              <h4 style={{ marginBlockEnd: '10px' }}>Plan adjustments and settlement</h4>

              {pcmaModifierSummary.length > 0 ? (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {pcmaModifierSummary.map((mod) => {
                    const displayName = PCMA_MODIFIER_DISPLAY_NAMES[mod.modifierType] ?? mod.modifierType;

                    const statusLabel = {
                      not_started:      'Not started',
                      draft:            'Draft',
                      pending_approval: 'Pending approval',
                      approved:         'Approved',
                    }[mod.modifierStatus] ?? mod.modifierStatus ?? 'Not started';

                    const statusClass = mod.modifierStatus === 'approved'
                      ? 'modifier-manager-badge modifier-manager-badge--complete'
                      : mod.modifierStatus === 'pending_approval'
                        ? 'modifier-manager-badge modifier-manager-badge--readonly'
                        : 'modifier-manager-badge modifier-manager-badge--not-created';

                    return (
                      <li
                        key={mod.modifierType}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '8px 12px',
                          borderBottom: '1px solid rgb(var(--color-border))',
                        }}
                      >
                        <div>
                          <strong style={{ fontSize: '0.9375rem' }}>{displayName}</strong>
                          <span style={{
                            marginInlineStart: '8px',
                            color: 'rgb(var(--color-text-muted))',
                            fontSize: '0.8125rem',
                          }}>
                            {mod.applicability === 'required' ? 'Required' : 'Optional'}
                          </span>
                        </div>
                        <span className={statusClass} style={{ flexShrink: 0 }}>
                          {statusLabel}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : orderedModifiers.length === 0 ? (
                <div className="modifier-manager-empty-state">
                  No modifier configuration is reported for this plan.
                </div>
              ) : (
                // WHY: fallback — modifiers from layer2Config jsonb when PCMA summary unavailable.
                // Displayed in the locked DP-7 canonical sequence using server-provided labels.
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {orderedModifiers.map((modifier) => {
                    const badge = modifierBadge(modifier);

                    return (
                      <li
                        key={`${modifier.modifierType}-${modifier.label}`}
                        className="modifier-manager-card"
                        style={{ marginBlockEnd: '8px' }}
                      >
                        <div className="modifier-manager-body">
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: '12px',
                              alignItems: 'center',
                            }}
                          >
                            <strong>{modifier.label}</strong>
                            <span className={badge.className}>
                              {badge.label}
                            </span>
                          </div>

                          <p
                            style={{
                              color: 'rgb(var(--color-text-muted))',
                              marginBlockEnd: 0,
                            }}
                          >
                            {modifier.configSummary || 'No configuration summary provided.'}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </section>

        <section aria-labelledby="plan-coverage">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              alignItems: 'center',
            }}
          >
            <h3 id="plan-coverage">Who does it cover, and when?</h3>

            <button
              type="button"
              className="text-button"
              aria-expanded={sectionExpanded.coverage}
              aria-controls="plan-coverage-content"
              onClick={() => handleToggleSection('coverage')}
            >
              {sectionExpanded.coverage ? 'Hide coverage' : 'Show coverage'}
            </button>
          </div>

          {/* WHY: coverage remains inspectable independently from economics because assignment scope and event-effective dates are authoritative document content. */}
          {sectionExpanded.coverage && (
            <div id="plan-coverage-content">
              {/* WHY: ep 22 router fields — confirmed S777. coverage{} object did not exist. */}
              <dl style={{ margin: '12px 0 8px', padding: 0 }}>
                <div style={dlRowStyle}>
                  <dt style={dtStyle}>Role</dt>
                  <dd style={ddStyle}>
                    {assignmentScope.roleName
                      ? `${assignmentScope.roleName}${assignmentScope.roleCode ? ` (${assignmentScope.roleCode})` : ''}`
                      : assignmentScope.roleId ?? 'Not specified'}
                  </dd>
                </div>
                <div style={dlRowStyle}>
                  <dt style={dtStyle}>Segment</dt>
                  <dd style={ddStyle}>
                    {assignmentScope.segmentName
                      ? `${assignmentScope.segmentName}${assignmentScope.segmentCode ? ` (${assignmentScope.segmentCode})` : ''}`
                      : assignmentScope.segmentId ?? 'Not specified'}
                  </dd>
                </div>
                <div style={dlRowStyle}>
                  <dt style={dtStyle}>Resolved population</dt>
                  <dd style={ddStyle}>{resolvedPopulation.resolvedCount ?? 0}</dd>
                </div>
                <div style={dlRowStyle}>
                  <dt style={dtStyle}>Coverage exceptions</dt>
                  <dd style={ddStyle}>{(resolvedPopulation.coverageExceptions || []).length} exception(s)</dd>
                </div>
                <div style={dlRowStyle}>
                  <dt style={dtStyle}>Effective from</dt>
                  <dd style={ddStyle}>{temporalRelationship.effectiveFrom || 'Not specified'}</dd>
                </div>
                <div style={{ ...dlRowStyle, borderBottom: 'none' }}>
                  <dt style={dtStyle}>Effective to</dt>
                  <dd style={ddStyle}>{temporalRelationship.effectiveTo || 'Open-ended'}</dd>
                </div>
              </dl>
            </div>
          )}
        </section>

        <section aria-labelledby="plan-impact">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              alignItems: 'center',
            }}
          >
            <h3 id="plan-impact">
              What is the expected economic consequence?
            </h3>

            <button
              type="button"
              className="text-button"
              aria-expanded={sectionExpanded.impact}
              aria-controls="plan-impact-content"
              onClick={() => handleToggleSection('impact')}
            >
              {sectionExpanded.impact ? 'Hide economic impact' : 'Show economic impact'}
            </button>
          </div>

          {/* WHY: projected consequence is a distinct review state and is never used as a proxy for document completeness. */}
          {sectionExpanded.impact && (
            <div id="plan-impact-content">
              <p>
                <strong>Projected total payout:</strong>{' '}
                {renderProjectedPayout(impact)}
              </p>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px',
                  alignItems: 'center',
                }}
              >
                {/* WHY: ep 22 router field is 'freshnessAt' not 'projectionFreshnessAt'. Router-confirmed S777. */}
                {/* WHY: ep 22 router field is 'isProvisional' bool not 'completenessState' string. */}
                <ProvisionalBadge
                  freshnessTimestamp={impact.freshnessAt}
                  label="Projected"
                  completenessState={impact.isProvisional ? 'provisional' : 'available'}
                />

                <DataFreshnessIndicator
                  signal="projection"
                  freshnessAt={impact.freshnessAt}
                  status={impact.isProvisional ? 'provisional' : 'ready'}
                />
              </div>

              <p>
                <strong>Affected positions:</strong>{' '}
                {/* WHY: ep 22 router field is 'positionCount' not 'affectedPositionCount'. Router-confirmed S777. */}
                {impact.positionCount ?? 0}
              </p>
            </div>
          )}
        </section>

        <section aria-labelledby="plan-data-context">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              alignItems: 'center',
            }}
          >
            <h3 id="plan-data-context">Data context</h3>

            <button
              type="button"
              className="text-button"
              aria-expanded={sectionExpanded.dataContext}
              aria-controls="plan-data-context-content"
              onClick={() => handleToggleSection('dataContext')}
            >
              {sectionExpanded.dataContext ? 'Hide data context' : 'Show data context'}
            </button>
          </div>

          <p className="modifier-manager-info">
            Data context — reflects runtime data health.
            Separate from plan document validity.
          </p>

          {/* WHY: runtime data health remains categorically separate from document validity even when data issues may affect calculation. */}
          {sectionExpanded.dataContext && (
            <div id="plan-data-context-content">
              {/* WHY: ep 22 router returns dataContext{quotaFreshness{status,freshnessAt}, creditFreshness{status,freshnessAt},
                  populationReadiness{status,resolvedCount,totalScopeCount,blockingIssueCount}}.
                  Flat freshnessAt/Status fields do not exist — router-confirmed S777. */}
              <DataFreshnessIndicator
                signal="quota"
                freshnessAt={dataContext.quotaFreshness?.freshnessAt ?? null}
                status={dataContext.quotaFreshness?.status ?? null}
              />

              <DataFreshnessIndicator
                signal="credits"
                freshnessAt={dataContext.creditFreshness?.freshnessAt ?? null}
                status={dataContext.creditFreshness?.status ?? null}
              />

              <div>
                <strong>Population readiness:</strong>{' '}
                {dataContext.populationReadiness?.status || 'Not assessed'}
              </div>

              {/* WHY: blockingIssueCount from populationReadiness — surfaces calculation risk without invalidating the plan document. */}
              {(dataContext.populationReadiness?.blockingIssueCount ?? 0) > 0 && (
                <div className="modifier-manager-warning">
                  {dataContext.populationReadiness.blockingIssueCount} blocking data issue(s) — plan may be approved
                  but calculation may be affected.
                </div>
              )}
            </div>
          )}
        </section>

        <section aria-labelledby="plan-governance">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              alignItems: 'center',
            }}
          >
            <h3 id="plan-governance">Governance</h3>

            <button
              type="button"
              className="text-button"
              aria-expanded={sectionExpanded.governance}
              aria-controls="plan-governance-content"
              onClick={() => handleToggleSection('governance')}
            >
              {sectionExpanded.governance ? 'Hide governance' : 'Show governance'}
            </button>
          </div>

          {/* WHY: governance audit context is inspectable separately from economics and runtime data. */}
          {sectionExpanded.governance && (
            <div id="plan-governance-content">
              <dl style={{ margin: '12px 0 8px', padding: 0 }}>
                <div style={dlRowStyle}>
                  <dt style={dtStyle}>Plan version</dt>
                  <dd style={ddStyle}>{reviewData.version}</dd>
                </div>
                <div style={dlRowStyle}>
                  <dt style={dtStyle}>Economic effective from</dt>
                  <dd style={ddStyle}>{temporalRelationship.effectiveFrom || 'Not specified'}</dd>
                </div>
                <div style={dlRowStyle}>
                  <dt style={dtStyle}>Economic effective to</dt>
                  <dd style={ddStyle}>{temporalRelationship.effectiveTo || 'Open-ended'}</dd>
                </div>
                <div style={dlRowStyle}>
                  <dt style={dtStyle}>Workflow status</dt>
                  <dd style={ddStyle}>{reviewData.planStatus || 'Not specified'}</dd>
                </div>
                <div style={dlRowStyle}>
                  <dt style={dtStyle}>Submitted by</dt>
                  <dd style={ddStyle}>{reviewData.submittedByName || reviewData.submittedBy || 'Not specified'}</dd>
                </div>
                <div style={{ ...dlRowStyle, borderBottom: 'none' }}>
                  <dt style={dtStyle}>Submitted at</dt>
                  <dd style={ddStyle}>{reviewData.submittedAt || 'Not specified'}</dd>
                </div>
              </dl>

              {/* WHY: crlOverride is null in current ep 22 router response — not a {designSource, crlOverrideNote} object.
                  Render only when non-null (future endpoint extension). Router-confirmed S777. */}
              {crlOverride != null && (
                <div className="modifier-manager-info">
                  <strong>CA governance record — CRL override</strong>
                  <p>{String(crlOverride)}</p>
                </div>
              )}

              {/* WHY: governance history from audit_log via ep 22. Each entry: {eventType, actorId, at}.
                  'at' is the field name — not 'occurredAt'. Router-confirmed S777. */}
              {governanceHistory.length > 0 && (
                <div>
                  <h4>Governance history</h4>
                  <ul>
                    {governanceHistory.map((entry, index) => (
                      <li key={entry.actorId ? `${entry.eventType}-${index}` : index}>
                        {entry.eventType}{' '}
                        {/* WHY: ep 22 now returns actorName resolved via user_account JOIN. S808 enrichment. */}
                        {(entry.actorName || entry.actorId) ? `by ${entry.actorName || entry.actorId}` : ''}{' '}
                        {/* WHY: router key is 'at' not 'occurredAt'. Router-confirmed S777. */}
                        {entry.at ? `at ${entry.at}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* WHY: version lineage not in ep 22 router response. Stated explicitly. */}
              {reviewData.version > 1 && (
                <div>
                  <h4>Version lineage</h4>
                  <p>Version lineage not available in current API response.</p>
                </div>
              )}
            </div>
          )}
        </section>

        {/* WHY: approval failure is positioned with the document-level actions because no governance state changed when the server rejected the request. */}
        {error && (
          <div className="modifier-manager-inline-error" role="alert">
            {error}
          </div>
        )}

        <footer className="modifier-manager-footer">
          {/* WHY: ep 22 router field is 'callerEligibility' not 'eligibility'. Router-confirmed S777. */}
          <ApprovalEligibilityGate
            eligibility={callerEligibility}
            blockedMessage="You are not eligible to approve this plan."
          >
            <button
              type="button"
              className="primary-button"
              disabled={approving}
              onClick={handleApprove}
            >
              {approving ? 'Approving…' : 'Approve plan'}
            </button>
          </ApprovalEligibilityGate>

          <button
            type="button"
            className="secondary-button"
            disabled={approving}
            onClick={handleReturn}
          >
            Return for Changes
          </button>
        </footer>
      </div>
    </section>
  );
}

// WHY: currentUser and approvalScope removed from PropTypes — component is self-sufficient via EP22.
// EP22 returns callerEligibility{eligible, blockedReason} server-side; no client-side eligibility
// props are required. currentUser and approvalScope were declared isRequired but never referenced
// in the function body, which would cause PropTypes warnings in the browser console.
PlanApprovalReview.propTypes = {
  planId: PropTypes.number.isRequired,
  // WHY: amendmentId is optional — present when the plan is a V_new amendment target,
  // absent for standalone plans. Controls EP14 vs EP9 routing in handleApprove().
  // Supplied by the approval queue parent from the queue item row. AD-66 + OD-18.
  amendmentId: PropTypes.string,
  onApprove: PropTypes.func.isRequired,
  onReturn: PropTypes.func.isRequired,
};

export default PlanApprovalReview;

/**
 * @example
 * <PlanApprovalReview
 *   planId={481}
 *   currentUser={currentUser}
 *   approvalScope={approvalScope}
 *   onApprove={() => refreshApprovalQueue()}
 *   onReturn={() => openReturnDrawer()}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK
 * - C-62 is explicitly labeled "for navigation and understanding"; structured sections are explicitly authoritative.
 * - Locked #1/#2: one complete plan document receives one approval action.
 * - XC-5: document content, projection health, runtime freshness, and readiness are rendered as distinct concepts.
 * - DP-12 / XC-1: server-rendered primarySpine, rate table name/version, summaries, and scope text are consumed without raw-config inference.
 * - DP-5: no rate_table_id or raw internal spine identifier is rendered.
 * - DP-7: Layer 2 modifier summaries are ordered by the locked canonical sequence.
 * - DP-4: projected payout includes ProvisionalBadge and projection freshness.
 * - C-46 renders population, quota, credits, and projection freshness independently.
 * - C-45 renders server-authoritative approval eligibility.
 * - XC-6: projected payout keeps available, not_available, and partial states separate.
 * - DP-10: onApprove fires only after HTTP 200.
 * - INV-3: planStatus is displayed only as workflow context; it is never used in effective-date, read-only, or section logic.
 * - INV-4: all temporal context is consumed from the server payload.
 * - XC-7 / DP-14: workflow and configuration states are expressed in text.
 * - DP-8 audit: no per-modifier or sub-document CA-2 approval framing exists.
 */

/**
 * MIGRATION FLAGS — S777
 * - plan.effective_from / plan.effective_to — pending m_tc_01 (Neon FROZEN).
 *   temporalRelationship{effectiveFrom,effectiveTo} wired correctly S777.
 * - economicImpact.projectedTotalPayout — null (OI-S760-PAYOUT-SUMMARY-SCHEMA open). renderProjectedPayout handles null.
 * - dataContext sub-objects (quotaFreshness, creditFreshness, populationReadiness) — partially null pending future joins.
 * - planSummary — NOT in ep 22 response. OI-S753-PLAN-SUMMARY-GEN-01 carried.
 * - layer2Modifiers — NOT in ep 22 response. Router returns raw layer2Config jsonb. orderedModifiers always empty.
 * - governanceHistory[].at — key confirmed S777 from approvals_router.py (not 'occurredAt').
 * - crlOverride — null in current router implementation. Rendered gracefully (null check).
 * - OI-S774-PLAN-APPROVAL-REVIEW-SHAPE — CLOSED S777. All 7 structural mismatches corrected.
 */

/**
 * ACCESSIBILITY NOTES
 * - Every collapsible structured section uses a button with aria-expanded and aria-controls.
 * - Narrative and data-context qualification text is always rendered, not hidden behind color or hover treatment.
 * - Definition lists preserve label/value relationships for economics, coverage, and governance.
 * - Approval eligibility explanation is delegated to ApprovalEligibilityGate.
 * - Mutation failures use role="alert".
 * - All modifier states are rendered as textual labels in addition to badge styling.
 */
